/**
 * VoiceLogAdapter — shared transcript-commit contract for voice engines.
 *
 * Plain factory (not a React hook). Engines wire onCommit to their conversationLog
 * state; streaming partials go through onLivePartial only.
 */

import { mergeAssistantTranscriptionChunk } from '../../features/patient/components/symptomCheck/voice-conversation/conversationContract';

/** Partial vs seeded assistant text below this similarity is a material difference. */
const ASSISTANT_FLUSH_SIMILARITY_THRESHOLD = 0.85;

function normalizeUserText(text) {
  return String(text || '').trim().toLowerCase();
}

function levenshteinDistance(a, b) {
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i += 1) matrix[i] = [i];
  for (let j = 0; j <= a.length; j += 1) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i += 1) {
    for (let j = 1; j <= a.length; j += 1) {
      const cost = b[i - 1] === a[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[b.length][a.length];
}

function levenshteinSimilarity(a, b) {
  const left = String(a || '');
  const right = String(b || '');
  if (!left && !right) return 1;
  if (!left || !right) return 0;
  const maxLen = Math.max(left.length, right.length);
  if (maxLen === 0) return 1;
  return 1 - levenshteinDistance(left, right) / maxLen;
}

function isLikelySameAssistantUtterance(a, b) {
  const normalizedA = normalizeUserText(a).replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ');
  const normalizedB = normalizeUserText(b).replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ');
  if (!normalizedA || !normalizedB) return false;
  if (normalizedA === normalizedB) return true;
  return normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA);
}

function makeLogEntry(speaker, text) {
  return {
    speaker,
    text: String(text || '').trim(),
    timestamp: Date.now(),
  };
}

/**
 * Creates a VoiceLogAdapter instance.
 *
 * @param {Object} opts
 * @param {function(entry: {speaker:'user'|'assistant',text:string,timestamp:number}): void} opts.onCommit
 * @param {function(partialText: string): void} opts.onLivePartial
 * @param {function(entry: Object): void} [opts.onBeforeCommit]
 * @returns {VoiceLogAdapterInstance}
 */
export function createVoiceLogAdapter({ onCommit, onLivePartial, onBeforeCommit }) {
  if (typeof onCommit !== 'function') {
    throw new Error('createVoiceLogAdapter requires onCommit');
  }
  if (typeof onLivePartial !== 'function') {
    throw new Error('createVoiceLogAdapter requires onLivePartial');
  }

  const log = [];
  let lastCommittedUserNorm = '';
  let assistantPartialBuffer = '';
  let seededAssistantText = '';

  function commitEntry(entry) {
    if (typeof onBeforeCommit === 'function') {
      onBeforeCommit(entry);
    }
    log.push(entry);
    onCommit(entry);
    return entry;
  }

  function clearAssistantPartialState() {
    assistantPartialBuffer = '';
    seededAssistantText = '';
  }

  function commitUserTurn(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed) return null;
    const norm = normalizeUserText(trimmed);
    if (norm === lastCommittedUserNorm) return null;
    lastCommittedUserNorm = norm;
    return commitEntry(makeLogEntry('user', trimmed));
  }

  function commitAssistantTurn(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed) return null;
    clearAssistantPartialState();
    return commitEntry(makeLogEntry('assistant', trimmed));
  }

  function absorbAssistantPartial(chunk) {
    assistantPartialBuffer = mergeAssistantTranscriptionChunk(
      assistantPartialBuffer,
      chunk,
      isLikelySameAssistantUtterance
    );
    onLivePartial(assistantPartialBuffer);
  }

  function flushAssistantPartial() {
    const buffered = String(assistantPartialBuffer || '').trim();
    if (!buffered) return null;

    const seeded = String(seededAssistantText || '').trim();
    if (
      seeded &&
      levenshteinSimilarity(buffered, seeded) >= ASSISTANT_FLUSH_SIMILARITY_THRESHOLD
    ) {
      return null;
    }

    clearAssistantPartialState();
    return commitEntry(makeLogEntry('assistant', buffered));
  }

  function seedAssistantTurn(expectedText) {
    const expected = String(expectedText || '').trim();
    seededAssistantText = expected;
    assistantPartialBuffer = expected;
  }

  function replayDeferredUserTurn(text) {
    return commitUserTurn(text);
  }

  function getLog() {
    return [...log];
  }

  function reset() {
    log.length = 0;
    lastCommittedUserNorm = '';
    clearAssistantPartialState();
  }

  return {
    commitUserTurn,
    commitAssistantTurn,
    absorbAssistantPartial,
    flushAssistantPartial,
    seedAssistantTurn,
    replayDeferredUserTurn,
    getLog,
    reset,
  };
}

const voiceLogAdapter = {
  createVoiceLogAdapter,
};

export default voiceLogAdapter;
